import { gql, useQuery, useMutation } from '@apollo/client';
import { useState, useMemo, useEffect } from 'react';
import { 
  Download, 
  FileText, 
  Settings, 
  Plus, 
  FileJson, 
  FileType, 
  Search,
  Layout,
  ChevronRight,
  Printer,
  Star,
  StarOff
} from 'lucide-react';
import { toast } from '@/components/ui/toast';
import { FORMATS } from './reportsTable';
import { ViewReport } from './viewReport';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from '@/lib/utils';
import { useUser } from '@/store/useUser';

interface Report {
  section: string;
  id: string;
  description: string;
  menuorder: number;
  name: string;
}

interface SectionMap {
  [key: string]: Report[];
}



const GET_PROGRAM_REPORT = gql`
  query GetProgramReport($report: String!, $format: String!) {
    getProgramReport(report: $report, format: $format) {
      documentType
      documentData
    }
  }
`;




const GET_REPORTS = gql`
  query GetReports {
    getReports {
      section
      id
      name
      menuorder      
    }
  }
`;

interface CreateReportData {
  section: string;
  name: string;
  menuorder: string;
  statement: string;
}

const REPORT_DEFAULT_FORMATS: Record<string, string> = {
  igt_path_ji_approved: 'xls',
};

export const ReportsCenter = () => {
  const [selectedReport, setSelectedReport] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
  const [searchQuery, setSearchQuery] = useState('');
  const [localFavorites, setLocalFavorites] = useState<string[]>([]);
  const { user, updateReportFavorites } = useUser();
  const { data: reportsData } = useQuery(GET_REPORTS);
  const [ jsonReport, setJsonReport ] = useState<any>([]);
  const [displayReport, setDisplayReport] = useState(false);

  // Sync local favorites with user settings
  useEffect(() => {
    if (user?.settings?.reportFavorites) {
      setLocalFavorites(user.settings.reportFavorites);
    }
  }, [user]);

  useEffect(() => {
    const defaultFormat = REPORT_DEFAULT_FORMATS[selectedReport];
    if (defaultFormat) {
      setSelectedFormat(defaultFormat);
    }
  }, [selectedReport]);

  // Toggle favorite status
  const toggleFavorite = async (reportId: string) => {
    const newFavorites = localFavorites.includes(reportId)
      ? localFavorites.filter(id => id !== reportId)
      : [...localFavorites, reportId];
    
    setLocalFavorites(newFavorites);
    
    const result = await updateReportFavorites(newFavorites);
    if (!result.success) {
      console.error('Failed to update favorites:', result.error);
    }
  };

  // Group and Filter Reports
  const groupedReports = useMemo(() => {
    if (!reportsData?.getReports) return {};

    const filtered = reportsData.getReports.filter((report: Report) => {
      const query = searchQuery.toLowerCase();
      return (
        report.name.toLowerCase().includes(query) ||
        report.section.toLowerCase().includes(query)
      );
    });

    const groups: SectionMap = {};

    // 1. Add Favorites Group
    const favReports = filtered.filter((r: Report) => localFavorites.includes(r.id));
    if (favReports.length > 0) {
        groups['Favorites'] = favReports;
    }

    // 2. Add Standard Sections
    filtered.reduce((acc: SectionMap, report: Report) => {
      if (!acc[report.section]) {
        acc[report.section] = [];
      }
      acc[report.section].push(report);
      return acc;
    }, groups);

    // Sort reports within sections
    Object.keys(groups).forEach(key => {
        groups[key].sort((a, b) => a.menuorder - b.menuorder);
    });

    return groups;
  }, [reportsData, searchQuery, localFavorites]);

  const selectedReportData = useMemo(() => {
    if (!reportsData?.getReports || !selectedReport) return null;
    return reportsData.getReports.find((r: Report) => r.id === selectedReport);
  }, [reportsData, selectedReport]);


  const handleDownload = () => {
    if (!selectedReport) {
      toast.error('Please select a report');
      return;
    }

    getReport({
      report: selectedReport,
      format: selectedFormat
    });

  };

  const handleView = () => {
    if (!selectedReport) {
      toast.error('Please select a report');
      return;
    }

    setDisplayReport(true)
    getReport({
      report: selectedReport,
      format: 'json'
    });

  };

  const { loading, refetch: getReport } = useQuery(GET_PROGRAM_REPORT, {
    skip: true,
    variables: {
      report: selectedReport,
      format: selectedFormat
    },
    onCompleted: (data) => {
      try {
        const { documentData, documentType } = data.getProgramReport;
        if (!documentData || !documentType) {
          throw new Error('Invalid report data received');
        }

        if (documentType === 'json'){
          return setJsonReport(JSON.parse(documentData));
        }

        const binaryStr = atob(documentData);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }

        
        const blob = new Blob([bytes], { type: documentType });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedReport}.${selectedFormat}`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast.error('Failed to download report');
        console.error('Download error:', err);
      }
    },
    onError: (error) => {
      toast.error(error.message);
    }
  }) as any;















  return (
    <div className="flex h-full gap-4 px-0 pb-4 sm:px-1 lg:px-1 xl:px-2">
      {/* Sidebar */}
	      <div className="app-page-shell flex w-80 flex-col overflow-hidden">
	        <div className="border-b border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)]">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="app-kicker mb-2 text-[var(--neutral-gray-120)]">Reporting</div>
                  <h1 className="text-2xl font-semibold text-[var(--neutral-gray-120)]">Reports Center</h1>
                </div>
             <div className="flex items-center gap-2">
             </div>
            </div>
            
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <Input
                type="text"
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10"
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {Object.entries(groupedReports).map(([section, reports]) => (
            <div key={section} className="mb-6">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {section}
              </h3>
              <div className="space-y-1">
                {reports.map((report: Report) => {
                  const isFavorite = localFavorites.includes(report.id);
                  return (
                    <div key={report.id} className="relative group/panel">
                        <button
                            onClick={() => setSelectedReport(report.id)}
                            className={cn(
                            "group flex w-full items-center rounded-xl border p-3 text-left transition-all duration-200",
                            selectedReport === report.id
                                ? "border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] text-[var(--azure-primary)] shadow-sm"
                                : "border-transparent text-gray-700 hover:bg-[var(--surface-muted)] hover:text-gray-900"
                            )}
                        >
                            <FileText className={cn(
                            "h-5 w-5 mr-3 flex-shrink-0",
                            selectedReport === report.id ? "text-[var(--azure-primary)]" : "text-gray-400 group-hover:text-gray-600"
                            )} />
                            <div className="flex-1 min-w-0 pr-6">
                            <div className="font-medium text-sm truncate">{report.name}</div>
                            {report.description && (
                                <div className="text-xs text-gray-500 mt-0.5 line-clamp-1 truncate">
                                    {report.description}
                                </div>
                            )}
                            </div>
                        </button>
                        <button
                            onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(report.id);
                            }}
                            className={cn(
                            "absolute top-2 right-2 p-1 rounded transition-all duration-200 opacity-0 group-hover/panel:opacity-100",
                            isFavorite 
                                ? "text-yellow-500 hover:text-yellow-600 opacity-100" 
                                : "text-gray-400 hover:text-yellow-500"
                            )}
                            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                        >
                            {isFavorite ? (
                            <Star className="w-4 h-4 fill-current" />
                            ) : (
                            <StarOff className="w-4 h-4" />
                            )}
                        </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {Object.keys(groupedReports).length === 0 && (
             <div className="text-center py-8 text-gray-500 text-sm">
                 No reports found.
             </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="app-page-shell flex flex-1 flex-col overflow-hidden">
          {selectedReport ? (
              <div className="flex-1 flex flex-col h-full overflow-hidden">
                   {/* Report Header */}
                   <div className="border-b border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-8 py-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">{selectedReportData?.name}</h2>
                                <p className="text-gray-500 mt-1">{selectedReportData?.description || 'No description available.'}</p>
                                <div className="flex items-center gap-2 mt-2">
                                    <Badge variant="secondary" className="font-mono text-xs text-gray-500">
                                        ID: {selectedReport}
                                    </Badge>
                                    <Badge variant="outline" className="text-gray-500">
                                        {selectedReportData?.section}
                                    </Badge>
                                </div>
                            </div>

                        </div>
                   </div>

                   {/* Report Configuration */}
                   <div className="flex-1 overflow-y-auto p-8">
                        <div className="max-w-3xl space-y-8">
                            {/* Format Selection */}
                            <section>
                                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <FileType className="h-4 w-4 text-gray-500" />
                                    Output Format
                                </h3>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    {FORMATS.map((format) => (
                                    <button
                                        key={format.value}
                                        onClick={() => setSelectedFormat(format.value)}
                                        className={cn(
                                            "relative flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all duration-200 hover:shadow-sm focus:outline-none",
                                            selectedFormat === format.value
                                            ? "border-[var(--azure-primary)] bg-[var(--surface-muted)] text-[var(--azure-primary)]"
                                            : "border-[var(--neutral-gray-30)] bg-white text-gray-600 hover:border-[var(--neutral-gray-40)] hover:bg-[var(--surface-muted)]"
                                        )}
                                    >
                                        <div className="mb-2 p-2 rounded-full bg-white/50">
                                            {format.value === 'pdf' ? <FileType size={20} /> :
                                            format.value === 'csv' || format.value === 'xls' ? <FileText size={20} /> :
                                            <FileText size={20} />}
                                        </div>
                                        <span className="text-sm font-medium">{format.label}</span>
                                        {selectedFormat === format.value && (
                                            <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[var(--azure-primary)]" />
                                        )}
                                    </button>
                                    ))}
                                </div>
                            </section>

                            <div className="border-t border-gray-100 my-8" />

                            {/* Actions */}
                            <section>
                                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <Printer className="h-4 w-4 text-gray-500" />
                                    Generate Report
                                </h3>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Button 
                                        className="min-w-[200px] h-12 px-8 text-base" 
                                        onClick={handleDownload}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <div className="flex items-center gap-2">
                                                <span className="h-4 w-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                                                Processing...
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <Download className="h-5 w-5" />
                                                Download File
                                            </div>
                                        )}
                                    </Button>

                                    <Button 
                                        variant="outline"
                                        className="h-12 px-8 text-base min-w-[200px]" 
                                        onClick={handleView}
                                        disabled={loading}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Search className="h-4 w-4" />
                                            Preview Data
                                        </div>
                                    </Button>
                                </div>
                                <p className="mt-3 text-sm text-gray-500">
                                    The report will be generated based on the current data snapshot. Large reports may take a few moments to process.
                                </p>
                            </section>
                        </div>
                   </div>
              </div>
          ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-gray-400">
                  <div className="mb-4 rounded-full bg-[var(--surface-muted)] p-6">
                      <Layout className="h-12 w-12 text-gray-300" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Report Selected</h3>
                  <p className="max-w-sm text-gray-500">
                      Select a report from the sidebar to view details, configure options, and download data.
                  </p>
              </div>
          )}
      </div>

      {displayReport && <ViewReport report={jsonReport} format={selectedFormat} loading={loading} onClose={()=> setDisplayReport(false)} />}
    </div>
  );
};

export default ReportsCenter;
